import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../hooks/useCurrency';
import { Product } from '../types';
import { customFieldService, CustomProductField } from '../services/configurationService';
import { X, Package, DollarSign, Box, Calendar, FileText, Tag, Ruler } from 'lucide-react';

interface ProductPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product;
  isLoading?: boolean;
}

export const ProductPreviewModal: React.FC<ProductPreviewModalProps> = ({
  isOpen,
  onClose,
  product,
  isLoading = false
}) => {
  const { theme } = useTheme();
  const { formatCurrency } = useCurrency();
  const [customFields, setCustomFields] = useState<CustomProductField[]>([]);
  const [isClosing, setIsClosing] = useState(false);

  // Log product data for debugging
  useEffect(() => {
    if (product && isOpen) {
      console.log('📦 ProductPreviewModal - Product Data Received:', {
        id: product.id,
        name: product.name,
        category: product.category,
        unit: product.unit,
        stock_quantity: product.stock_quantity,
        purchase_price: product.purchase_price,
        selling_price: product.selling_price,
        description: product.description,
        brand: product.brand,
        status: product.status,
        allKeys: Object.keys(product)
      });
    }
  }, [product, isOpen]);

  useEffect(() => {
    const loadCustomFields = async () => {
      try {
        const fields = await customFieldService.getAll();
        setCustomFields(fields.filter(f => f.is_active));
      } catch (error) {
        console.error('Failed to load custom fields:', error);
      }
    };
    
    if (isOpen) {
      setIsClosing(false);
      loadCustomFields();
    }
  }, [isOpen, product]);

  if (!isOpen && !isClosing) return null;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 150);
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return theme === 'dark' 
          ? 'bg-green-900/50 text-green-300 border-green-700'
          : 'bg-green-50 text-green-700 border-green-200';
      case 'inactive':
        return theme === 'dark'
          ? 'bg-red-900/50 text-red-300 border-red-700'
          : 'bg-red-50 text-red-700 border-red-200';
      case 'out_of_stock':
        return theme === 'dark'
          ? 'bg-orange-900/50 text-orange-300 border-orange-700'
          : 'bg-orange-50 text-orange-700 border-orange-200';
      default:
        return theme === 'dark'
          ? 'bg-gray-700 text-gray-300 border-gray-600'
          : 'bg-gray-50 text-gray-700 border-gray-200';
    }
  };

  return createPortal(
    <div 
      className={`fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-2 sm:p-4 z-[9999] overflow-y-auto ${
        isClosing ? 'animate-fadeOut' : 'animate-fadeIn'
      }`}
      onClick={handleClose}
    >
      <div className={`w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-lg shadow-2xl my-auto ${
        isClosing ? 'animate-scaleOut' : 'animate-scaleIn'
      } ${theme === 'dark' ? 'bg-slate-800' : 'bg-white'
      }`} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={`px-2.5 sm:px-3 md:px-4 py-2.5 sm:py-3 border-b flex-shrink-0 ${
          theme === 'dark' ? 'border-slate-700 bg-slate-800' : 'border-slate-200 bg-gray-50'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 min-w-0 flex-1">
              <div className={`p-1.5 rounded-lg flex-shrink-0 ${
                theme === 'dark' ? 'bg-blue-900/50' : 'bg-blue-100'
              }`}>
                <Package className={`w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 ${
                  theme === 'dark' ? 'text-blue-400' : 'text-blue-600'
                }`} />
              </div>
              <div className="min-w-0 flex-1">
                <h2 className={`text-sm sm:text-base md:text-lg font-semibold truncate ${
                  theme === 'dark' ? 'text-white' : 'text-slate-900'
                }`}>
                  Product Details
                </h2>
                <p className={`text-xs truncate ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  View complete product information
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className={`p-2 rounded-lg transition-colors touch-manipulation flex-shrink-0 ${
                theme === 'dark' 
                  ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-2.5 sm:p-3 md:p-4 overflow-y-auto" style={{ maxHeight: 'calc(85vh - 60px)' }}>
          {isLoading ? (
            // Loading Skeleton
            <div className="space-y-4">
              {/* Title Skeleton */}
              <div className="space-y-2">
                <div className={`h-6 w-48 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
                <div className={`h-4 w-32 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
              </div>

              {/* Basic Info Grid Skeleton */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[...Array(6)].map((_, idx) => (
                  <div key={idx} className={`p-3 sm:p-4 rounded-lg ${
                    theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                  }`}>
                    <div className={`h-4 w-16 rounded-lg animate-pulse mb-2 ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                    <div className={`h-5 w-24 rounded-lg animate-pulse ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                  </div>
                ))}
              </div>              {/* Custom Fields Skeleton */}
              <div className="space-y-3">
                <div className={`h-5 w-40 rounded-lg animate-pulse ${
                  theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
                {[...Array(3)].map((_, idx) => (
                  <div key={idx} className="space-y-2">
                    <div className={`h-4 w-32 rounded-lg animate-pulse ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                    <div className={`h-6 w-full rounded-lg animate-pulse ${
                      theme === 'dark' ? 'bg-gray-700' : 'bg-gray-200'
                    }`} />
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-4 animate-content-fade">
              {/* Product Name & Status */}
              <div className="transition-all duration-300 ease-out">
                <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-4">
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-lg sm:text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {product.name}
                    </h3>
                    <p className={`text-xs sm:text-sm mt-1 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>
                      by {product.brand}
                    </p>
                  </div>
                  <span className={`px-2.5 sm:px-3 py-1 text-xs font-medium rounded-full border capitalize ${getStatusColor(product.status)}`}>
                    {product.status?.replace('_', ' ') || 'Unknown'}
                  </span>
                </div>
              </div>

              {/* Basic Information */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className={`p-3 sm:p-4 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                }`}>
                  <div className="flex items-center space-x-2 mb-2">
                    <Tag className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
                    <span className={`text-xs font-medium uppercase tracking-wide ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>Category</span>
                  </div>
                  <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {product.category || 'N/A'}
                  </p>
                </div>

                <div className={`p-3 sm:p-4 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                }`}>
                  <div className="flex items-center space-x-2 mb-2">
                    <Ruler className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
                    <span className={`text-xs font-medium uppercase tracking-wide ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>Unit</span>
                  </div>
                  <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {product.unit || 'N/A'}
                  </p>
                </div>
              </div>

              {/* Pricing */}
              <div className={`p-3 sm:p-4 rounded-lg ${
                theme === 'dark' ? 'bg-blue-900/20 border border-blue-800' : 'bg-blue-50 border border-blue-200'
              }`}>
                <div className="flex items-center space-x-2 mb-3">
                  <DollarSign className={`w-4 sm:w-5 h-4 sm:h-5 ${theme === 'dark' ? 'text-blue-400' : 'text-blue-600'}`} />
                  <h4 className={`text-xs sm:text-sm font-semibold uppercase tracking-wide ${
                    theme === 'dark' ? 'text-blue-400' : 'text-blue-700'
                  }`}>Pricing</h4>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <p className={`text-xs ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'}`}>Purchase Price</p>
                    <p className={`text-base sm:text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {product.purchase_price ? formatCurrency(product.purchase_price) : 'N/A'}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'}`}>Selling Price</p>
                    <p className={`text-base sm:text-lg font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {product.selling_price ? formatCurrency(product.selling_price) : 'N/A'}
                    </p>
                  </div>
                </div>
                {product.selling_price && product.purchase_price && (
                  <div className="mt-3 pt-3 border-t border-blue-700/30">
                    <p className={`text-xs ${theme === 'dark' ? 'text-blue-300' : 'text-blue-600'}`}>Profit Margin</p>
                    <p className={`text-base sm:text-lg font-bold ${
                      product.selling_price > product.purchase_price 
                        ? 'text-green-500' 
                        : 'text-red-500'
                    }`}>
                      {formatCurrency(product.selling_price - product.purchase_price)}
                      <span className="text-xs sm:text-sm ml-1">
                        ({Math.round(((product.selling_price - product.purchase_price) / product.purchase_price) * 100)}%)
                      </span>
                    </p>
                  </div>
                )}
              </div>

              {/* Inventory */}
              <div className={`p-3 sm:p-4 rounded-lg ${
                theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
              }`}>
                <div className="flex items-center space-x-2 mb-3">
                  <Box className={`w-4 sm:w-5 h-4 sm:h-5 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
                  <h4 className={`text-xs sm:text-sm font-semibold uppercase tracking-wide ${
                    theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                  }`}>Inventory</h4>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Current Stock</p>
                    <p className={`text-xl sm:text-2xl font-bold ${
                      product.stock_quantity <= (product.minimum_stock_level || 10)
                        ? 'text-red-500'
                        : theme === 'dark' ? 'text-white' : 'text-gray-900'
                    }`}>
                      {product.stock_quantity}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Min. Stock Level</p>
                    <p className={`text-xl sm:text-2xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                      {product.minimum_stock_level || 10}
                    </p>
                  </div>
                </div>
                {product.stock_quantity <= (product.minimum_stock_level || 10) && (
                  <div className={`mt-3 p-2 rounded ${
                    theme === 'dark' ? 'bg-red-900/50 border border-red-700' : 'bg-red-50 border border-red-200'
                  }`}>
                    <p className={`text-xs ${theme === 'dark' ? 'text-red-300' : 'text-red-700'}`}>
                      ⚠️ Stock level is low. Consider reordering.
                    </p>
                  </div>
                )}
              </div>

              {/* Expiry Date */}
              {product.expiry_date && (
                <div className={`p-3 sm:p-4 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                }`}>
                  <div className="flex items-center space-x-2 mb-2">
                    <Calendar className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
                    <span className={`text-xs font-medium uppercase tracking-wide ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>Best Before Date</span>
                  </div>
                  <p className={`text-sm font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                    {formatDate(product.expiry_date)}
                  </p>
                </div>
              )}

              {/* Description */}
              {product.description && (
                <div className={`p-3 sm:p-4 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                }`}>
                  <div className="flex items-center space-x-2 mb-2">
                    <FileText className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
                    <span className={`text-xs font-medium uppercase tracking-wide ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>Description</span>
                  </div>
                  <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    {product.description}
                  </p>
                </div>
              )}

              {/* Custom Fields */}
              {customFields.length > 0 && (
                <div className={`p-3 sm:p-4 rounded-lg ${
                  theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
                }`}>
                  <div className="flex items-center space-x-2 mb-3">
                    <Tag className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
                    <h4 className={`text-xs font-semibold uppercase tracking-wide ${
                      theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                    }`}>Additional Information</h4>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {customFields.map((field) => {
                      // Get custom fields object, safely handle undefined
                      const customFieldsData = product.custom_fields || {};
                      
                      // Try both field_key and id for compatibility
                      const fieldValue = customFieldsData[field.field_key] || 
                                        customFieldsData[field.id] || 
                                        '';
                      
                      // Show all fields, even if empty
                      return (
                        <div key={field.id}>
                          <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>
                            {field.field_name}
                          </p>
                          <p className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                            {fieldValue ? (
                              field.field_type === 'checkbox' 
                                ? (fieldValue === 'true' || fieldValue === '1' || fieldValue === 'on' ? 'Yes' : 'No')
                                : field.field_type === 'date'
                                  ? formatDate(fieldValue)
                                  : String(fieldValue)
                            ) : (
                              <span className={theme === 'dark' ? 'text-gray-600' : 'text-gray-400'}>
                                Not set
                              </span>
                            )}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Timestamps */}
              <div className={`p-3 sm:p-4 rounded-lg ${
                theme === 'dark' ? 'bg-gray-700/50' : 'bg-gray-50'
              }`}>
                <h4 className={`text-xs font-semibold uppercase tracking-wide mb-3 ${
                  theme === 'dark' ? 'text-gray-400' : 'text-gray-600'
                }`}>Timestamps</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>Created</p>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      {formatDate(product.created_at)}
                    </p>
                  </div>
                  <div>
                    <p className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-500'}`}>Last Updated</p>
                    <p className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                      {formatDate(product.updated_at)}
                    </p>
                  </div>
                </div>
            </div>
          </div>
            )}
        </div>          {/* Footer */}
          <div className={`px-2.5 sm:px-3 md:px-4 py-2.5 sm:py-3 border-t flex-shrink-0 ${
            theme === 'dark' ? 'border-slate-700 bg-slate-800/50' : 'border-slate-200 bg-gray-50'
          }`}>
            <button
              onClick={onClose}
              className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium rounded-md transition-colors touch-manipulation ${
                theme === 'dark'
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              Close
            </button>
          </div>
        </div>
      </div>,
    document.body
  );
};

export default ProductPreviewModal;
